import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, FileText } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';
import ScopeBoundary from '../components/ScopeBoundary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PolicyGenerator() {
  const [step, setStep] = useState('form'); // form, email, results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    organization_name: '',
    industry: '',
    employee_count: '',
    ai_tools: [],
    ai_uses: [],
    email: ''
  });
  const [result, setResult] = useState(null);

  const industries = [
    'Technology', 'Healthcare', 'Education', 'Financial Services', 
    'Government', 'Professional Services', 'Other'
  ];

  const employeeCounts = ['1–10', '11–50', '51–200', '201–1000', '1000+'];

  const aiTools = [
    'ChatGPT/OpenAI', 'Google Gemini', 'Microsoft Copilot', 
    'Claude/Anthropic', 'GitHub Copilot', 'Midjourney/image tools', 
    'Other', 'Unsure'
  ];

  const aiUses = [
    'Content creation', 'Customer service', 'Code generation',
    'Data analysis', 'Internal productivity', 'Client deliverables', 'Other'
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMultiSelect = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.organization_name || !formData.industry || !formData.employee_count) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.ai_tools.length === 0) {
      setError('Please select at least one AI tool');
      return;
    }
    setError('');
    setStep('email');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/tools/policy-generator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source_tool: 'policy-generator'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
        setStep('results');
      } else {
        setError(data.detail || 'Failed to generate policy');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      <main className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/free-tools" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#102A43] mb-8 text-sm">
            <ArrowLeft size={16} />
            Back to Free Tools
          </Link>

          {step === 'form' && (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
                  FREE TOOL
                </p>
                <h1 className="text-[36px] font-bold text-[#102A43] mb-4 leading-tight">
                  Generate a basic AI Acceptable Use Policy for your organization — free
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Answer five questions and get a customizable AI policy document you can use today.
                </p>
              </div>

              <form onSubmit={handleFormSubmit} className="bg-white border border-[#E2E8F0] rounded-[10px] p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-2">
                      1. Organization Name *
                    </label>
                    <input
                      type="text"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      placeholder="Enter your organization name"
                      required
                      data-testid="policy-org-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-2">
                      2. Industry *
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      required
                      data-testid="policy-industry"
                    >
                      <option value="">Select industry</option>
                      {industries.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-2">
                      3. Number of Employees *
                    </label>
                    <select
                      name="employee_count"
                      value={formData.employee_count}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                      required
                      data-testid="policy-employee-count"
                    >
                      <option value="">Select range</option>
                      {employeeCounts.map(count => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-2">
                      4. Which AI tools does your organization use? *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {aiTools.map(tool => (
                        <button
                          key={tool}
                          type="button"
                          onClick={() => handleMultiSelect('ai_tools', tool)}
                          className={`px-4 py-2 text-sm rounded-md border transition-colors text-left ${
                            formData.ai_tools.includes(tool)
                              ? 'bg-[#2F80ED] text-white border-[#2F80ED]'
                              : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2F80ED]'
                          }`}
                        >
                          {tool}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#102A43] mb-2">
                      5. What is AI primarily used for?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {aiUses.map(use => (
                        <button
                          key={use}
                          type="button"
                          onClick={() => handleMultiSelect('ai_uses', use)}
                          className={`px-4 py-2 text-sm rounded-md border transition-colors text-left ${
                            formData.ai_uses.includes(use)
                              ? 'bg-[#2F80ED] text-white border-[#2F80ED]'
                              : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2F80ED]'
                          }`}
                        >
                          {use}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                <button
                  type="submit"
                  className="w-full mt-8 bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors flex items-center justify-center gap-2"
                  data-testid="policy-continue-btn"
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </form>
            </>
          )}

          {step === 'email' && (
            <>
              <div className="text-center mb-12">
                <h1 className="text-[32px] font-bold text-[#102A43] mb-4">
                  Where should we send your policy?
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Enter your email to receive your customized AI Acceptable Use Policy.
                </p>
              </div>

              <form onSubmit={handleEmailSubmit} className="bg-white border border-[#E2E8F0] rounded-[10px] p-8">
                <div>
                  <label className="block text-sm font-medium text-[#102A43] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#2F80ED]"
                    placeholder="you@company.com"
                    required
                    data-testid="policy-email"
                  />
                </div>

                {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-[#2F80ED] text-white font-semibold py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors disabled:opacity-50"
                  data-testid="policy-generate-btn"
                >
                  {loading ? 'Generating...' : 'Generate My Policy'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('form')}
                  className="w-full mt-3 text-[#64748B] hover:text-[#102A43] text-sm"
                  data-testid="policy-back-btn"
                >
                  ← Back to questions
                </button>
              </form>
            </>
          )}

          {step === 'results' && result && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#F0FDF4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="text-[#15803D]" size={32} />
                </div>
                <h1 className="text-[32px] font-bold text-[#102A43] mb-2">
                  Your AI Acceptable Use Policy is ready
                </h1>
                <p className="text-[16px] text-[#64748B]">
                  Customized for {formData.organization_name}
                </p>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-[18px] font-bold text-[#102A43]">
                      AI Acceptable Use Policy
                    </h2>
                    <p className="text-[14px] text-[#64748B]">
                      PDF document · Sent to {formData.email}
                    </p>
                  </div>
                  {result.download_url && (
                    <a
                      href={result.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-5 py-2 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                  )}
                </div>

                <ScopeBoundary
                  delivers={[
                    'One AI Acceptable Use Policy document',
                    'Customized with your organization name, industry, and tools in use'
                  ]}
                  doesNotDeliver={[
                    'AI Tool Inventory',
                    'AI Risk Register',
                    'AI Incident Log',
                    'Employee AI Agreement',
                    'Vendor Assessment Template',
                    'Accountability Checklist',
                    'Governance Summary Report'
                  ]}
                />

                <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-[10px] p-6">
                  <p className="text-[15px] text-[#102A43] mb-4">
                    Your AI Acceptable Use Policy is ready. A policy is the first document — not the full picture. The AI Accountability Documentation System includes 7 additional templates that turn your policy into a complete governance posture. $97 one-time.
                  </p>
                  <Link
                    to="/documentation-system"
                    className="inline-flex items-center gap-2 bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
                  >
                    Get the Documentation System — $97
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <MainFooter />
    </div>
  );
}

export default PolicyGenerator;
